// The account API (worker/src/account.ts), with this browser's key as the
// bearer. A 401 means the key no longer names anyone (the handle changed or
// the account was deleted), so it is dropped and the caller lands on sign in.

import type { AccountError } from "@breeze/core";
import { clearUserKey, getUserKey } from "./storage";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: AccountError["field"];

  constructor(status: number, body: Partial<AccountError> | null) {
    super(body?.message ?? "Something went wrong. Try again.");
    this.status = status;
    this.code = body?.code ?? "error";
    this.field = body?.field;
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal; keepalive?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const key = getUserKey();
  if (key) headers.authorization = `Bearer ${key}`;
  if (init.body !== undefined) headers["content-type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? "GET",
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal: init.signal,
      keepalive: init.keepalive,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, { message: "No connection. Try again." });
  }
  const body = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    // Only the Worker saying the key is dead signs this browser out. A 503
    // (the database blinked) and every other failure keep it.
    if (res.status === 401 && key && (body as Partial<AccountError> | null)?.code === "signed_out") clearUserKey();
    throw new ApiError(res.status, body as Partial<AccountError> | null);
  }
  return body as T;
}
